import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Checkbox, Icon, Text } from '@mntn/ui-components'
import type React from 'react'
import { useMemo } from 'react'
import { useFilter } from '../context/filter-context'

const OFFERING_TYPE_OPTIONS = [
  { id: 'deal', name: 'Deal' },
  { id: 'bundle', name: 'Bundle' },
] as const

export const OfferingTypeFilter: React.FC = () => {
  const { filters, updateFilter, isSectionOpen, toggleSection } = useFilter()
  const isOpen = isSectionOpen('offering-type')

  const selectedTypes = useMemo(() => filters.offeringTypes || [], [filters.offeringTypes])

  const handleTypeChange = (typeId: (typeof OFFERING_TYPE_OPTIONS)[number]['id'], checked: boolean) => {
    const updatedTypes = checked ? [...selectedTypes, typeId] : selectedTypes.filter(id => id !== typeId)

    updateFilter('offeringTypes', updatedTypes.length > 0 ? updatedTypes : undefined)
  }

  const selectedCount = selectedTypes.length
  const allSelected = OFFERING_TYPE_OPTIONS.length > 0 && selectedCount === OFFERING_TYPE_OPTIONS.length

  const handleSelectAll = () => {
    if (allSelected) {
      updateFilter('offeringTypes', undefined)
    } else {
      updateFilter(
        'offeringTypes',
        OFFERING_TYPE_OPTIONS.map(type => type.id),
      )
    }
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={isOpen ? 'offering-type' : ''}
      onValueChange={() => toggleSection('offering-type')}
      className="w-full"
    >
      <AccordionItem value="offering-type" className="border-0">
        <AccordionTrigger className="p-6 border-b border-borders-primary hover:no-underline">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Text variant="lg-sb">Deal/Bundle Type</Text>
              {selectedCount > 0 && (
                <div className="bg-primary text-header-inverse rounded-full px-2 py-1 text-xs font-medium">
                  {selectedCount}
                </div>
              )}
            </div>
            <Icon
              name={isOpen ? 'Minus' : 'Plus'}
              className="h-6 w-6 text-primary shrink-0 transition-transform duration-200"
            />
          </div>
        </AccordionTrigger>
        <AccordionContent className="p-6 border-b border-borders-primary">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox id="select-all-types" checked={allSelected} onCheckedChange={handleSelectAll} />
              <Text variant="sm-sb" className="text-header">
                Select All ({OFFERING_TYPE_OPTIONS.length})
              </Text>
            </div>

            <div className="space-y-3">
              {OFFERING_TYPE_OPTIONS.map(type => (
                <div key={type.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={`type-${type.id}`}
                    checked={selectedTypes.includes(type.id)}
                    onCheckedChange={checked =>
                      // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
                      handleTypeChange(type.id, checked as boolean)
                    }
                  />
                  <Text variant="base-sm" className="text-header">
                    {type.name}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
